import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { LogOut, Loader2, Search, Users, UserCheck, Mic2, ChevronRight, User, UserPlus, Megaphone, AlertTriangle } from "lucide-react";
import AdminArtistView from "./AdminArtistView";
import { AddArtistModal } from "./AddArtistModal";
import { InviteRemindersCard } from "./InviteRemindersCard";

const FILTERS = [
  { key: "all", label: "ALL" },
  { key: "no_poster", label: "NO POSTER YET" },
  { key: "broken", label: "BROKEN POSTER" },
  { key: "unannounced", label: "NOT ANNOUNCED" },
  { key: "announced", label: "ANNOUNCED" },
];

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LDC_LOGO = "https://customer-assets.emergentagent.com/job_artist-portal-ldc/artifacts/39dze3hq_Text%20Logo%20Gold_noBG_WEBP.webp";

export const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—";

export default function AdminDashboard({ token, onLogout, onMyProfile, hasOwnProfile }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [openId, setOpenId] = useState(null);
  const [filter, setFilter] = useState("all");
  const [showAdd, setShowAdd] = useState(false);
  const [queue, setQueue] = useState([]);

  const openArtist = (id, list) => {
    setQueue((list || filtered).map((a) => a.record_id));
    setOpenId(id);
  };

  const authHeader = { Authorization: `Bearer ${token}` };

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/admin/roster`, { headers: authHeader });
      setData(res.data);
    } catch (err) {
      if (err.response?.status === 401) onLogout();
      else setError("Failed to load roster. Please retry.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!data) return [];
    const q = query.trim().toLowerCase();
    let list = data.artists;
    if (filter === "no_poster") list = list.filter((a) => !a.has_poster);
    else if (filter === "broken") list = list.filter((a) => a.dead_posters > 0);
    else if (filter === "unannounced") list = list.filter((a) => !a.announced);
    else if (filter === "announced") list = list.filter((a) => a.announced);
    if (!q) return list;
    return list.filter((a) =>
      a.full_name.toLowerCase().includes(q) || a.email.includes(q) || a.band_name.toLowerCase().includes(q)
    );
  }, [data, query, filter]);

  if (openId) {
    return (
      <AdminArtistView
        recordId={openId}
        token={token}
        onBack={() => setOpenId(null)}
        onChanged={load}
        onLogout={onLogout}
        queue={queue}
        queueLabel={FILTERS.find((f) => f.key === filter)?.label}
        onNavigate={setOpenId}
      />
    );
  }

  return (
    <div className="dashboard admin-dash" data-testid="admin-dashboard">
      <header className="dash-header">
        <img src={LDC_LOGO} alt="London Drumstick Co." className="dash-logo" />
        <div className="dash-header-right">
          <span className="admin-badge" data-testid="admin-badge">ADMIN</span>
          {hasOwnProfile && (
            <button className="btn-announce" onClick={onMyProfile} data-testid="my-profile-btn">
              <User size={15} /> MY PROFILE
            </button>
          )}
          <span className="dash-email" data-testid="user-email">{localStorage.getItem("ldc_email")}</span>
          <button className="btn-logout" onClick={onLogout} data-testid="logout-btn">
            <LogOut size={16} /> LOGOUT
          </button>
        </div>
      </header>

      <main className="admin-main">
        <div className="admin-title-row">
          <div>
            <p className="admin-kicker">ARTIST ROSTER</p>
            <h1 className="admin-title">Artist Management</h1>
          </div>
          <button className="btn-download" onClick={() => setShowAdd(true)} data-testid="add-artist-btn">
            <UserPlus size={16} /> ADD ARTIST
          </button>
        </div>

        {loading && (
          <div className="loading-screen loading-inline" data-testid="admin-loading">
            <Loader2 size={36} className="spin gold" />
            <p>LOADING ROSTER...</p>
          </div>
        )}
        {error && (
          <div className="error-screen" data-testid="admin-error">
            <p>{error}</p>
            <button className="btn-primary" onClick={load}>RETRY</button>
          </div>
        )}

        {data && !loading && (
          <>
            <section className="admin-stats">
              <LatestCard label="LATEST ADDED TO ROSTER" item={data.latest_record} date={data.latest_record?.created_time} onOpen={(id) => openArtist(id, data.artists)} testId="latest-record-card" />
              <LatestCard label="LATEST PORTAL SIGN-UP" item={data.latest_signup} date={data.latest_signup?.account_created_at} onOpen={(id) => openArtist(id, data.artists)} testId="latest-signup-card" />
              <div className="admin-counters">
                <Counter icon={<Users size={18} />} value={data.stats.total} label="ARTISTS" testId="stat-total" />
                <Counter icon={<UserCheck size={18} />} value={data.stats.with_account} label="PORTAL ACCOUNTS" testId="stat-accounts" />
                <Counter icon={<Mic2 size={18} />} value={data.stats.hireable} label="HIREABLE" testId="stat-hireable" />
              </div>
              <InviteRemindersCard token={token} />
            </section>

            <div className="admin-filters" data-testid="roster-filters">
              {FILTERS.map((f) => {
                const count = f.key === "all" ? data.stats.total
                  : f.key === "no_poster" ? data.stats.no_poster
                  : f.key === "broken" ? data.stats.dead_posters
                  : f.key === "announced" ? data.stats.announced
                  : data.stats.total - data.stats.announced;
                return (
                  <button
                    key={f.key}
                    className={`tag-option ${filter === f.key ? "active" : ""}`}
                    onClick={() => setFilter(f.key)}
                    data-testid={`filter-${f.key}`}
                  >
                    {f.key === "no_poster" && <Megaphone size={13} />}{f.key === "broken" && <AlertTriangle size={13} />} {f.label} <span className="filter-count">{count}</span>
                  </button>
                );
              })}
            </div>

            <div className="admin-search-wrap">
              <Search size={18} className="admin-search-icon" />
              <input
                className="admin-search"
                placeholder="Search by name, email or band…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                data-testid="roster-search"
              />
              <span className="admin-search-count" data-testid="roster-count">{filtered.length} / {data.artists.length}</span>
            </div>

            <RosterTable artists={filtered} onOpen={(id) => openArtist(id)} />
          </>
        )}
      </main>

      {showAdd && (
        <AddArtistModal
          token={token}
          onClose={() => setShowAdd(false)}
          onCreated={(recordId) => { setShowAdd(false); load(); setQueue([recordId]); setOpenId(recordId); }}
        />
      )}
    </div>
  );
}

function LatestCard({ label, item, date, onOpen, testId }) {
  return (
    <button className="latest-card" onClick={() => item && onOpen(item.record_id)} disabled={!item} data-testid={testId}>
      <p className="latest-label">{label}</p>
      {item ? (
        <div className="latest-body">
          <Thumb src={item.thumb} name={item.full_name} size={64} />
          <div className="latest-info">
            <p className="latest-name">{item.full_name || "Unnamed"}</p>
            <p className="latest-band">{item.band_name || "—"}</p>
            <p className="latest-date">{fmtDate(date)}</p>
          </div>
          <ChevronRight size={18} className="latest-chev" />
        </div>
      ) : (
        <p className="latest-empty">No data yet</p>
      )}
    </button>
  );
}

function Counter({ icon, value, label, testId }) {
  return (
    <div className="admin-counter" data-testid={testId}>
      <span className="admin-counter-icon">{icon}</span>
      <span className="admin-counter-value">{value}</span>
      <span className="admin-counter-label">{label}</span>
    </div>
  );
}

export function Thumb({ src, name, size = 44 }) {
  const initials = (name || "?").split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
  return src ? (
    <img src={src} alt={name} className="roster-thumb" style={{ width: size, height: size }} />
  ) : (
    <div className="roster-thumb roster-thumb-empty" style={{ width: size, height: size }}>{initials}</div>
  );
}

function RosterTable({ artists, onOpen }) {
  if (artists.length === 0) {
    return <p className="roster-empty" data-testid="roster-empty">No artists match that search.</p>;
  }
  return (
    <div className="roster-table-wrap">
      <table className="roster-table" data-testid="roster-table">
        <thead>
          <tr>
            <th>ARTIST</th>
            <th>BAND / ACT</th>
            <th>EMAIL</th>
            <th>HIREABLE</th>
            <th>POSTER</th>
            <th>ANNOUNCED</th>
            <th>PHOTOS</th>
            <th>JOINED</th>
            <th>PORTAL</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {artists.map((a) => (
            <tr key={a.record_id} onClick={() => onOpen(a.record_id)} data-testid={`roster-row-${a.record_id}`}>
              <td>
                <div className="roster-artist-cell">
                  <Thumb src={a.thumb} name={a.full_name} />
                  <span className="roster-name">{a.full_name || "Unnamed"}</span>
                </div>
              </td>
              <td className="roster-band">{a.band_name || "—"}</td>
              <td className="roster-email">{a.email || "—"}</td>
              <td>{a.hireable ? <span className="pill pill-green">YES</span> : <span className="pill pill-muted">NO</span>}</td>
              <td>
                {a.dead_posters > 0
                  ? <span className="pill pill-red" data-testid={`broken-pill-${a.record_id}`}>{a.dead_posters} BROKEN</span>
                  : a.has_poster ? <span className="pill pill-gold">SAVED</span> : <span className="pill pill-muted">NONE</span>}
              </td>
              <td>{a.announced ? <span className="pill pill-green">YES</span> : <span className="pill pill-warn">NO</span>}</td>
              <td className="roster-num">{a.photo_count}</td>
              <td className="roster-date">{fmtDate(a.created_time)}</td>
              <td>{a.has_account ? <span className="pill pill-gold">ACTIVE</span> : <span className="pill pill-muted">NONE</span>}</td>
              <td><ChevronRight size={16} className="roster-chev" /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
